import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildLiveQuotes,
  formatFxRate,
} from "../Pages/Private/ContaInternacional/constants";
import { fetchMarketQuotes } from "../Services/fiatMarketsApi";

const DEFAULT_CURRENCIES = "USD,EUR,PYG,JPY";

/** BCB PTAX quotes JPY per 100 units; API may return per-100 or per-1. */
export const resolveMarketQuotePrice = (item) => {
  const price = Number(item?.price);
  const unit = Number(item?.unit) || 1;

  if (!Number.isFinite(price)) return 0;
  if (unit <= 1) return price;

  // e.g. price 3.1975 + unit 100 → 0.031975 per JPY
  if (price >= 1) return price / unit;

  // e.g. price 0.031975 + unit 100 → already per 1 JPY
  return price;
};

export const mapMarketQuotesResponse = (response) => {
  const data = response?.data ?? response;
  const quoteCurrency = data?.quote_currency ?? "BRL";
  const items = Array.isArray(data?.items) ? data.items : [];

  return items
    .filter((item) => item?.available !== false && item?.currency)
    .map((item) => {
      const currency = String(item.currency).toUpperCase();
      const rate = resolveMarketQuotePrice(item);

      return {
        id: `${currency.toLowerCase()}-${quoteCurrency.toLowerCase()}`,
        asset: currency.toLowerCase(),
        pair: `${currency} / ${quoteCurrency}`,
        price: formatFxRate(rate),
        change: null,
        positive: null,
      };
    });
};

export const useFxQuotes = ({ autoRefreshMs = 8000 } = {}) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const inFlightRef = useRef(false);

  const loadQuotes = useCallback(async ({ silent = false } = {}) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    if (!silent) {
      setLoading(true);
      setRefreshing(false);
    } else {
      setRefreshing(true);
    }

    try {
      const response = await fetchMarketQuotes(DEFAULT_CURRENCIES, "auto");
      const mapped = mapMarketQuotesResponse(response);

      if (mapped.length === 0) {
        throw new Error("Empty quotes response");
      }

      setQuotes(mapped);
      setUsingFallback(false);
    } catch {
      setQuotes(buildLiveQuotes());
      setUsingFallback(true);
    } finally {
      inFlightRef.current = false;
      if (!silent) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  useEffect(() => {
    if (!autoRefreshMs) return undefined;

    const interval = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      loadQuotes({ silent: true });
    }, autoRefreshMs);

    return () => clearInterval(interval);
  }, [autoRefreshMs, loadQuotes]);

  return {
    quotes,
    loading,
    refreshing,
    usingFallback,
    reload: () => loadQuotes(),
  };
};
